/**
 * Ticket Comment Thread Component
 * Displays comments with internal/external indicators and add comment form
 */

import { useState } from 'react'
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Divider,
  Avatar,
  Checkbox,
  FormControlLabel,
  Alert,
  Chip,
  MenuItem,
} from '@mui/material'
import { Lock, Send } from '@mui/icons-material'
import { TicketCommentDto, AddCommentRequest, TicketCommentParty, TicketCommentPartyLabels } from '@api/types/tickets'
import { safeFormatDistanceToNow } from '@utils/dateUtils'
import { TicketPhotoGrid, TicketPhotoPicker } from './TicketPhotos'
import { useTicketPhotos } from './useTicketPhotos'

interface TicketCommentThreadProps {
  comments: TicketCommentDto[]
  onAddComment: (request: AddCommentRequest) => Promise<void>
  canAddInternalComments?: boolean
  isSubmitting?: boolean
  /**
   * Ownership claims, moderators: offer "Visible to" (Everyone / Claimant only / Owner only) and
   * tag each comment with its side. The claimant and the owners never see each other's notes.
   */
  showParty?: boolean
}

const partyTag: Record<TicketCommentParty, string | null> = {
  [TicketCommentParty.Everyone]: null,
  [TicketCommentParty.Claimant]: 'CLAIMANT SIDE',
  [TicketCommentParty.Owner]: 'OWNER SIDE',
}

export function TicketCommentThread({
  comments,
  onAddComment,
  canAddInternalComments = false,
  isSubmitting = false,
  showParty = false,
}: TicketCommentThreadProps) {
  const [newComment, setNewComment] = useState('')
  const [isInternal, setIsInternal] = useState(false)
  const [party, setParty] = useState<TicketCommentParty>(TicketCommentParty.Everyone)
  const [error, setError] = useState<string | null>(null)
  const photos = useTicketPhotos()

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)

    if (photos.uploading) {
      setError('Wait for the photos to finish uploading')
      return
    }

    if (!newComment.trim() && photos.readyIds.length === 0) {
      setError('Write a comment or add a photo')
      return
    }

    try {
      await onAddComment({
        content: newComment.trim(),
        isInternal: canAddInternalComments ? isInternal : false,
        attachmentIds: photos.readyIds.length > 0 ? photos.readyIds : undefined,
        party: showParty ? party : undefined,
      })

      setNewComment('')
      setIsInternal(false)
      setParty(TicketCommentParty.Everyone)
      photos.clear()
    } catch (err: any) {
      setError(err?.response?.data?.error || err.message || 'Failed to add comment')
    }
  }

  const sortedComments = [...comments].sort(
    (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
  )

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Comments ({comments.length})
      </Typography>

      {/* Comments List */}
      <Box sx={{ mb: 3 }}>
        {sortedComments.length === 0 ? (
          <Paper sx={{ p: 3, textAlign: 'center', bgcolor: 'background.default' }}>
            <Typography variant="body2" color="text.secondary">
              No comments yet. Be the first to comment!
            </Typography>
          </Paper>
        ) : (
          sortedComments.map((comment) => (
            <Box key={comment.id}>
              <Paper
                sx={{
                  p: { xs: 1.5, sm: 2 },
                  mb: { xs: 1.5, sm: 2 },
                  bgcolor: comment.isInternal ? 'warning.light' : 'background.paper',
                  border: comment.isInternal ? '2px solid' : 'none',
                  borderColor: 'warning.main',
                }}
              >
                <Box sx={{ display: 'flex', gap: { xs: 1.25, sm: 2 } }}>
                  <Avatar sx={{ bgcolor: 'primary.main', width: { xs: 32, sm: 40 }, height: { xs: 32, sm: 40 }, fontSize: { xs: '0.9rem', sm: '1.25rem' } }}>
                    {(comment.authorDisplayName || 'U')[0].toUpperCase()}
                  </Avatar>

                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, flexWrap: 'wrap' }}>
                      <Typography variant="subtitle2" fontWeight="bold">
                        {comment.authorDisplayName || 'Unknown User'}
                      </Typography>

                      {comment.isInternal && (
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                            color: 'warning.dark',
                            fontSize: '0.75rem',
                          }}
                        >
                          <Lock fontSize="small" />
                          <Typography variant="caption" fontWeight="bold">
                            INTERNAL
                          </Typography>
                        </Box>
                      )}

                      {comment.party !== undefined && partyTag[comment.party] && (
                        <Chip
                          size="small"
                          color={comment.party === TicketCommentParty.Owner ? 'info' : 'secondary'}
                          label={partyTag[comment.party]}
                          sx={{ height: 20, fontSize: '0.7rem' }}
                        />
                      )}

                      <Typography variant="caption" color="text.secondary">
                        {safeFormatDistanceToNow(comment.createdAt)}
                      </Typography>
                    </Box>

                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
                      {comment.content}
                    </Typography>

                    <TicketPhotoGrid attachments={comment.attachments} size={80} />
                  </Box>
                </Box>
              </Paper>
            </Box>
          ))
        )}
      </Box>

      <Divider sx={{ mb: 3 }} />

      {/* Add Comment Form */}
      <Paper sx={{ p: { xs: 1.5, sm: 2 } }}>
        <Typography variant="subtitle2" gutterBottom>
          Add Comment
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Box component="form" onSubmit={handleSubmit}>
          <TextField
            fullWidth
            multiline
            minRows={3}
            maxRows={8}
            placeholder="Write your comment..."
            value={newComment}
            onChange={(e) => setNewComment(e.target.value)}
            disabled={isSubmitting}
            sx={{ mb: 1 }}
          />

          <Box sx={{ mb: 2 }}>
            <TicketPhotoPicker photos={photos.photos} onAdd={photos.add} onRemove={photos.remove} disabled={isSubmitting} />
          </Box>

          {showParty && (
            <TextField
              select
              size="small"
              label="Visible to"
              value={party}
              onChange={(e) => setParty(Number(e.target.value) as TicketCommentParty)}
              disabled={isSubmitting || isInternal}
              helperText={isInternal ? 'Internal notes are seen by staff only' : 'The claimant and the owner never see each other’s notes'}
              sx={{ mb: 2, width: { xs: '100%', sm: 'auto' }, minWidth: { sm: 200 } }}
            >
              {[TicketCommentParty.Everyone, TicketCommentParty.Claimant, TicketCommentParty.Owner].map((p) => (
                <MenuItem key={p} value={p}>
                  {TicketCommentPartyLabels[p]}
                </MenuItem>
              ))}
            </TextField>
          )}

          {/* Wraps on a phone: the long checkbox label and the button do not fit one 360px row. */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
            {canAddInternalComments && (
              <FormControlLabel
                control={
                  <Checkbox checked={isInternal} onChange={(e) => setIsInternal(e.target.checked)} size="small" />
                }
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Lock fontSize="small" />
                    <Typography variant="body2">Internal note (staff only)</Typography>
                  </Box>
                }
                disabled={isSubmitting}
              />
            )}

            <Button
              type="submit"
              variant="contained"
              endIcon={<Send />}
              disabled={isSubmitting || photos.uploading || (!newComment.trim() && photos.readyIds.length === 0)}
              sx={{ ml: 'auto' }}
            >
              {isSubmitting ? 'Posting...' : 'Post Comment'}
            </Button>
          </Box>
        </Box>
      </Paper>
    </Box>
  )
}
